import { useState, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import SignatureCanvas from 'react-signature-canvas'
import { Check, CheckCircle } from 'lucide-react'
import api from '../../services/api'

/**
 * LeveringsbonSectie — Inline sectie voor LEVERING jobs.
 * Toont leveringsregels met geleverd-toggle en klantondertekening.
 */
export default function LeveringsbonSectie({ projectId, jobId }) {
  const [toonOndertekening, setToonOndertekening] = useState(false)
  const [regelStatus, setRegelStatus] = useState({})

  const { data: bon, isLoading, refetch } = useQuery({
    queryKey: ['leveringsbon-project', projectId],
    queryFn: async () => {
      const res = await api.get(`/leveringsbonnen?project_id=${projectId}`)
      return res.data?.[0] ?? null
    },
    enabled: !!projectId,
  })

  useEffect(() => {
    if (bon?.leveringsregels) {
      const init = {}
      bon.leveringsregels.forEach((r, i) => {
        init[i] = r.geleverd ?? false
      })
      setRegelStatus(init)
    }
  }, [bon])

  const toggleRegel = async (index) => {
    const nieuwStatus = !regelStatus[index]
    setRegelStatus(prev => ({ ...prev, [index]: nieuwStatus }))

    const bijgewerkt = (bon.leveringsregels || []).map((r, i) =>
      i === index ? { ...r, geleverd: nieuwStatus } : r
    )
    try {
      await api.patch(`/leveringsbonnen/${bon.id}`, { regels: bijgewerkt })
    } catch {
      setRegelStatus(prev => ({ ...prev, [index]: !nieuwStatus }))
    }
  }

  const onderteken = async (handtekeningDataUrl, ondertekendDoor) => {
    await api.post(`/leveringsbonnen/${bon.id}/ondertekenen`, {
      handtekening_klant: handtekeningDataUrl,
      ondertekend_door: ondertekendDoor,
      ondertekend_op: new Date().toISOString(),
    })
    refetch()
    setToonOndertekening(false)
  }

  return (
    <div className="bg-white rounded-lg shadow border border-line dark:border-gray-700 p-4">
      <h2 className="text-lg font-semibold text-gray-900 mb-3">
        Leveringsbon
        {bon?.leveringsbon_nummer && (
          <span className="ml-2 text-sm font-normal text-gray-500">
            {bon.leveringsbon_nummer}
          </span>
        )}
      </h2>

      {isLoading && <p className="text-sm text-gray-400">Laden&hellip;</p>}

      {!bon && !isLoading && (
        <p className="text-sm text-gray-400 italic">
          Geen leveringsbon gevonden voor dit project.
        </p>
      )}

      {bon && (
        <>
          {/* Regellijst */}
          <div className="rounded-lg border border-gray-200 overflow-hidden mb-3">
            {(bon.leveringsregels ?? []).map((regel, i) => (
              <div
                key={i}
                className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 last:border-0"
              >
                <button
                  onClick={() => toggleRegel(i)}
                  className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                    regelStatus[i]
                      ? 'bg-green-500 border-green-500 text-white'
                      : 'border-gray-300'
                  }`}
                >
                  {regelStatus[i] && <Check size={14} />}
                </button>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${
                    regelStatus[i] ? 'line-through text-gray-400' : 'text-gray-900'
                  }`}>
                    {regel.naam || regel.artikel}
                  </p>
                  <p className="text-xs text-gray-400">
                    {regel.aantal} {regel.eenheid}
                    {regel.opmerking && ` \u2014 ${regel.opmerking}`}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Status + ondertekening */}
          {bon.ondertekend_door_ontvanger ? (
            <div className="bg-green-50 rounded-lg p-4 flex items-center gap-2">
              <CheckCircle size={20} className="text-green-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-green-800">
                  Ondertekend door {bon.ontvangen_door}
                </p>
                <p className="text-xs text-green-600">
                  {bon.ondertekend_datum
                    ? new Date(bon.ondertekend_datum).toLocaleDateString('nl-BE')
                    : ''}
                </p>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setToonOndertekening(true)}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-medium text-sm"
            >
              Laat klant ondertekenen
            </button>
          )}
        </>
      )}

      {toonOndertekening && (
        <LeveringsbonSignDialog
          isOpen={toonOndertekening}
          onClose={() => setToonOndertekening(false)}
          onSubmit={onderteken}
        />
      )}
    </div>
  )
}


function LeveringsbonSignDialog({ isOpen, onClose, onSubmit }) {
  const sigRef = useRef(null)
  const [naam, setNaam] = useState('')
  const [error, setError] = useState(null)
  const [bezig, setBezig] = useState(false)

  if (!isOpen) return null

  const handleSubmit = async () => {
    setError(null)
    if (!naam.trim()) { setError('Naam vereist'); return }
    if (sigRef.current?.isEmpty()) { setError('Handtekening vereist'); return }
    setBezig(true)
    try {
      await onSubmit(sigRef.current.toDataURL('image/png'), naam.trim())
    } catch {
      setError('Mislukt. Probeer opnieuw.')
    } finally {
      setBezig(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end">
      <div className="bg-white rounded-t-2xl w-full p-6">
        <h3 className="text-base font-semibold mb-4">Handtekening klant</h3>
        <input
          value={naam}
          onChange={e => setNaam(e.target.value)}
          placeholder="Naam ondertekenaar"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-3"
        />
        <div className="border-2 border-dashed border-gray-300 rounded-lg mb-3 bg-gray-50">
          <SignatureCanvas
            ref={sigRef}
            canvasProps={{ className: 'w-full h-40' }}
            backgroundColor="rgb(249,250,251)"
            penColor="black"
          />
        </div>
        {error && <p className="text-red-500 text-xs mb-2">{error}</p>}
        <div className="flex gap-2">
          <button
            onClick={() => sigRef.current?.clear()}
            className="flex-1 py-2 border border-gray-300 rounded-lg text-sm text-gray-600"
          >
            Wis
          </button>
          <button
            onClick={onClose}
            className="flex-1 py-2 border border-gray-300 rounded-lg text-sm text-gray-600"
          >
            Annuleer
          </button>
          <button
            onClick={handleSubmit}
            disabled={bezig}
            className="flex-[2] py-2 bg-blue-600 text-white rounded-lg text-sm font-medium px-6 disabled:bg-blue-300"
          >
            {bezig ? 'Bezig\u2026' : 'Bevestig'}
          </button>
        </div>
      </div>
    </div>
  )
}
